import React from "react";
import { Banner, Button, FormField, Input, Select, StatusPill } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import type { StaffInvitation } from "@/api";
import { useLiveInvalidation } from "@/live/live-provider";
import { useLang, useT } from "../i18n";
import { invitationRows, staffInvitationUrl } from "./invitation.logic";
import type { PushToast } from "@/notifications/toast-queue";
import styles from "./invitation-panel.module.css";

/**
 * How a business adds somebody to itself (#1298).
 *
 * The same three blocks as the owner's handover panel (#1164) — the link just made, the links so
 * far, the form for the next one — and the same one fact behind the shape: the link is the whole
 * credential, so it exists in readable form exactly once, in the response to creating it. There is
 * no "show it again", because the server keeps only a hash.
 *
 * What is different is what it replaces. Before this, adding a colleague meant an admin typing
 * somebody else's first password and then telling them what it was. Here nobody but the colleague
 * ever knows it, which is why the form has no password field at all — its absence is the feature.
 *
 * Since #1304 the server also emails the link to the address it was issued for, so the copy field
 * is the fallback rather than the delivery mechanism. It stays exactly as it was: the mail can
 * fail, and when it does the panel says so instead of leaving the admin to assume it went.
 */

/** How long "Kopiert" stays on the button before it offers to copy again. */
const COPIED_FEEDBACK_MS = 2000;

interface FreshLink {
  url: string;
  invitedEmail: string;
  expiresAt: string;
  /**
   * Whether the server emailed the link to the colleague (#1304). Reported rather than assumed:
   * "we sent it" and "send it to them yourself" are different instructions, and an admin who
   * believes the first when the second is true leaves somebody waiting for a mail nobody sent.
   */
  emailSent: boolean;
}

function pillTone(state: StaffInvitation["state"]): "success" | "neutral" | "warning" {
  if (state === "outstanding") return "success";
  if (state === "accepted") return "neutral";
  return "warning";
}

/** Newest first: the link somebody is looking for is almost always the one they just made. */
export function sortStaffInvitations(rows: StaffInvitation[]): StaffInvitation[] {
  return [...rows].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function StaffInvitationPanel({ pushToast }: { pushToast: PushToast }) {
  const t = useT();
  const { lang } = useLang();
  // #1536: the links from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<StaffInvitation[] | null>(() => {
    const remembered = cachedRead("listStaffInvitations");
    return remembered ? sortStaffInvitations(invitationRows<StaffInvitation>(remembered)) : null;
  });
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [role, setRole] = React.useState("operator");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  /** Held in memory only, and only until the screen is left. The server cannot reproduce it. */
  const [fresh, setFresh] = React.useState<FreshLink | null>(null);
  const [copied, setCopied] = React.useState(false);
  const copyRowRef = React.useRef<HTMLDivElement>(null);

  const formatDate = React.useCallback(
    (iso: string) => new Date(iso).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB"),
    [lang],
  );

  /**
   * Live refetches can overlap, and they arrive on somebody else's write — so this is written the
   * way the roster beside it is (#1298).
   *
   * Only the newest response may paint: otherwise a slow refetch that started before a revoke can
   * land after it and put the withdrawn link back on screen as outstanding, which is exactly the
   * stale row this panel exists to prevent.
   *
   * And a failure keeps the rows it already has. Replacing them with an empty list would make the
   * panel say "no invitation created yet" — a positive claim, from a request that failed. Nothing
   * else would ever correct it, because a refetch only happens when somebody writes; an admin who
   * believed it would issue a second link, and issuing revokes the one already sent.
   */
  const sequence = React.useRef(0);
  const load = React.useCallback(() => {
    const mine = ++sequence.current;
    return cockpit.listStaffInvitations().then(
      (list) => {
        if (mine !== sequence.current) return;
        setRows(sortStaffInvitations(invitationRows<StaffInvitation>(list)));
        setError(null);
      },
      (e: unknown) => {
        if (mine !== sequence.current) return;
        setError((e as { message?: string })?.message ?? "unknown");
      },
    );
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  // The invitations are part of the `staff` domain, beside the roster (#1298). Two admins in the
  // same business must not have to reload to see what the other just issued or withdrew — and a
  // stale "outstanding" row invites somebody to send a link that no longer works.
  const invalidations = useLiveInvalidation("staff");
  React.useEffect(() => {
    if (invalidations === 0) return;
    load();
  }, [invalidations, load]);

  // The moment a link exists, the next thing to do is copy it, so that is where focus goes.
  React.useEffect(() => {
    if (!fresh) return;
    copyRowRef.current?.querySelector("button")?.focus();
  }, [fresh]);

  // "Kopiert" reverts on its own; a stale confirmation on a button is a small lie.
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const outstandingForEmail =
    rows?.find(
      (row) => row.state === "outstanding" && row.invitedEmail === email.trim().toLowerCase(),
    ) ?? null;

  const issue = () => {
    setBusy(true);
    cockpit
      .issueStaffInvitation({ email: email.trim(), name: name.trim() || undefined, role })
      .then((issued) => {
        setCopied(false);
        setFresh({
          url: staffInvitationUrl(issued.token, window.location.origin, import.meta.env.BASE_URL || "/"),
          invitedEmail: issued.invitedEmail,
          expiresAt: issued.expiresAt,
          emailSent: issued.emailSent,
        });
        setEmail("");
        setName("");
        setRole("operator");
        load();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusy(false));
  };

  const revoke = (invitationId: string) => {
    setBusy(true);
    cockpit
      .revokeStaffInvitation(invitationId)
      .then(() => {
        setFresh(null);
        load();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusy(false));
  };

  const selectWholeLink = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.currentTarget.select?.();
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(fresh.url);
      setCopied(true);
      pushToast({ tone: "success", title: t("invite.owner.copied") });
    } catch {
      // Selecting the text is the honest fallback: the person can still copy with the keyboard,
      // and the toast says so instead of pretending the copy happened.
      copyRowRef.current?.querySelector("input")?.select();
      pushToast({ tone: "warning", title: t("invite.owner.copyFailed") });
    }
  };

  // #2252: an accepted invitation has done its job; the person is on the staff list now.
  const listed = rows?.filter((row) => row.state !== "accepted") ?? null;

  return (
    <div className={styles.panel} data-testid="staff-invitation-panel">
      <div className={styles.intro}>
        <div className="neo-eyebrow">{t("staffInvite.title")}</div>
        <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
          {t("staffInvite.hint")}
        </p>
      </div>

      {fresh && (
        <section
          className={styles.current}
          aria-labelledby="staff-invitation-current-title"
          data-testid="staff-invitation-current"
        >
          <h3 id="staff-invitation-current-title" className={styles.sectionTitle}>
            {t("staffInvite.currentLink", { email: fresh.invitedEmail })}
          </h3>
          <div className={styles.copyRow} ref={copyRowRef}>
            <Input
              readOnly
              value={fresh.url}
              aria-label={t("staffInvite.currentLinkLabel")}
              onFocus={selectWholeLink}
              onClick={selectWholeLink}
              data-testid="staff-invitation-link-field"
            />
            <div className={styles.copyAction}>
              <Button variant="primary" size="sm" onClick={copy} data-testid="staff-invitation-copy">
                {copied ? t("invite.owner.copiedShort") : t("invite.owner.copy")}
              </Button>
            </div>
          </div>
          {/* Two different weights on purpose. "It went" is a footnote — nothing is left to do.
              "It did not" is an instruction, and an instruction the admin must not scroll past:
              the link above is now the only way this colleague gets in, and it is readable only
              while this panel is open. */}
          {fresh.emailSent ? (
            <p className={styles.meta} data-testid="staff-invitation-delivery">
              {t("staffInvite.emailed", { email: fresh.invitedEmail })}
            </p>
          ) : (
            <Banner
              tone="warning"
              title={t("staffInvite.notEmailed")}
              data-testid="staff-invitation-delivery"
            />
          )}
          <p className={styles.meta}>
            {t("invite.owner.expires", { date: formatDate(fresh.expiresAt) })}
            {" · "}
            {t("staffInvite.once")}
          </p>
        </section>
      )}

      <section aria-labelledby="staff-invitation-history-title" data-testid="staff-invitation-history">
        <h3 id="staff-invitation-history-title" className={styles.sectionTitle}>
          {t("staffInvite.history")}
        </h3>
        {/* Beside whatever was last read, not instead of it: the list on screen may be a moment
            old, and saying so is the honest version of a refetch that did not arrive. */}
        {error && (
          <Banner tone="danger" title={t("common.error")} data-testid="staff-invitation-error">
            {error}
          </Banner>
        )}
        {rows && rows.length === 0 && (
          <p className={`subtle ${styles.empty}`}>{t("staffInvite.none")}</p>
        )}
        {rows && rows.length > 0 && listed?.length === 0 && (
          <p className={`subtle ${styles.empty}`} data-testid="staff-invitation-all-accepted">{t("staffInvite.allAccepted")}</p>
        )}
        {listed && listed.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table} data-testid="staff-invitation-table">
              <thead>
                <tr>
                  <th scope="col">{t("invite.owner.col.status")}</th>
                  <th scope="col">{t("invite.owner.col.invited")}</th>
                  <th scope="col">{t("staff.colRole")}</th>
                  <th scope="col">{t("invite.owner.col.expires")}</th>
                  <th scope="col">{t("invite.owner.col.action")}</th>
                </tr>
              </thead>
              <tbody>
                {listed.map((row) => (
                  <tr key={row.id} data-testid="staff-invitation-row" data-state={row.state}>
                    <td data-label={t("invite.owner.col.status")}>
                      <StatusPill tone={pillTone(row.state)} label={t(`invite.state.${row.state}`)} />
                    </td>
                    <td data-label={t("invite.owner.col.invited")}>
                      {row.invitedName && <span className={styles.invitedName}>{row.invitedName}</span>}
                      <span className={styles.invitedEmail}>{row.invitedEmail}</span>
                    </td>
                    <td data-label={t("staff.colRole")}>{t(`role.${row.role}`, undefined, row.role)}</td>
                    <td data-label={t("invite.owner.col.expires")} className="neo-num">
                      {formatDate(row.expiresAt)}
                    </td>
                    <td data-label={t("invite.owner.col.action")} className={styles.actionCell}>
                      {row.state === "outstanding" && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => revoke(row.id)}
                          data-testid="staff-invitation-revoke"
                        >
                          {t("invite.owner.revoke")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section
        className={styles.form}
        aria-labelledby="staff-invitation-create-title"
        data-testid="staff-invitation-create"
      >
        <h3 id="staff-invitation-create-title" className={styles.sectionTitle}>
          {t("staffInvite.createTitle")}
        </h3>
        <div className={styles.fields}>
          <FormField label={t("staffInvite.emailLabel")} htmlFor="staff-invite-issue-email" required>
            <Input
              id="staff-invite-issue-email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="name@acme-catering.de"
            />
          </FormField>
          <FormField label={t("staffInvite.nameLabel")} htmlFor="staff-invite-issue-name">
            <Input
              id="staff-invite-issue-name"
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            />
          </FormField>
          {/* No `htmlFor`/`id` pair here, matching the create form above: the bundle's Select does
              not forward an id, so a label pointing at one would point at nothing. */}
          <FormField label={t("staff.colRole")}>
            <Select
              value={role}
              options={[
                { value: "operator", label: t("staff.roleOptionOperator") },
                { value: "admin", label: t("staff.roleOptionAdmin") },
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value)}
            />
          </FormField>
        </div>
        {outstandingForEmail && (
          <Banner
            tone="warning"
            title={t("staffInvite.reissueWarning")}
            data-testid="staff-invitation-reissue-warning"
          />
        )}
        <div className={styles.formActions}>
          <Button
            variant="primary"
            size="sm"
            disabled={busy || !email.trim()}
            onClick={issue}
            data-testid="staff-invitation-issue"
          >
            {t("staffInvite.issue")}
          </Button>
        </div>
      </section>
    </div>
  );
}
